/*
 * Administradores: listagem, criacao, edicao e exclusao. Exercita permissoes, CSRF e paginacao
 * ponta a ponta.
 */
import { useEffect, useState } from 'react';
import {
  PERMISSIONS,
  ROLE_KEY_VALUES,
  ROLE_LABELS,
  type AdminUserDto,
  type RoleKey,
} from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { MikrotikDto } from '../lib/types';
import { Alert, Button, Card, Field, Spinner } from '../components/ui';

export function AdminsPage() {
  const { can, session } = useAuth();
  const podeEscrever = can(PERMISSIONS.ADMIN_WRITE);

  const [admins, setAdmins] = useState<AdminUserDto[] | null>(null);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [mikrotiks, setMikrotiks] = useState<MikrotikDto[]>([]);

  // Form de criacao
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'atendimento' as RoleKey,
    allMikrotiks: true,
    mikrotikIds: [] as number[],
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [formOk, setFormOk] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Modais de acao
  const [editingAdmin, setEditingAdmin] = useState<AdminUserDto | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    role: 'atendimento' as RoleKey,
    isActive: true,
    allMikrotiks: true,
    mikrotikIds: [] as number[],
  });
  const [editingSubmitting, setEditingSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const [resetPwdAdmin, setResetPwdAdmin] = useState<AdminUserDto | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const [deletingAdmin, setDeletingAdmin] = useState<AdminUserDto | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  async function carregar() {
    setErroLista(null);
    try {
      const { data } = await apiList<AdminUserDto>('/admin/admins');
      setAdmins(data);
    } catch (caught) {
      setAdmins([]);
      setErroLista(
        caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar a lista',
      );
    }
  }

  useEffect(() => {
    void carregar();
    apiList<MikrotikDto>('/admin/mikrotiks?perPage=100')
      .then(({ data }) => setMikrotiks(data))
      .catch(() => setMikrotiks([]));
  }, []);

  function toggleMikrotik(id: number) {
    setForm((prev) => {
      const existe = prev.mikrotikIds.includes(id);
      return {
        ...prev,
        mikrotikIds: existe
          ? prev.mikrotikIds.filter((item) => item !== id)
          : [...prev.mikrotikIds, id],
      };
    });
  }

  function toggleEditMikrotik(id: number) {
    setEditForm((prev) => {
      const existe = prev.mikrotikIds.includes(id);
      return {
        ...prev,
        mikrotikIds: existe
          ? prev.mikrotikIds.filter((item) => item !== id)
          : [...prev.mikrotikIds, id],
      };
    });
  }

  async function criar(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFormOk(null);

    if (!form.allMikrotiks && form.mikrotikIds.length === 0) {
      setFormError('Selecione ao menos um equipamento para o acesso restrito.');
      return;
    }

    setSubmitting(true);

    try {
      const criado = await apiRequest<AdminUserDto>('/admin/admins', {
        method: 'POST',
        body: form,
      });
      setFormOk(
        `${criado.name} criado. A troca de senha sera exigida no primeiro acesso dele.`,
      );
      setForm({
        name: '',
        email: '',
        password: '',
        role: 'atendimento',
        allMikrotiks: true,
        mikrotikIds: [],
      });
      await carregar();
    } catch (caught) {
      setFormError(
        caught instanceof ApiRequestError ? caught.displayMessage : 'Nao foi possivel criar',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function abrirEdicao(admin: AdminUserDto) {
    setModalError(null);
    setEditingAdmin(admin);
    setEditForm({
      name: admin.name,
      role: admin.role,
      isActive: admin.isActive,
      allMikrotiks: admin.allMikrotiks,
      mikrotikIds: admin.allowedMikrotikIds ?? [],
    });
  }

  async function salvarEdicao(event: React.FormEvent) {
    event.preventDefault();
    if (!editingAdmin) return;
    setModalError(null);

    if (!editForm.allMikrotiks && editForm.mikrotikIds.length === 0) {
      setModalError('Selecione ao menos um equipamento para o acesso restrito.');
      return;
    }

    setEditingSubmitting(true);
    try {
      await apiRequest<AdminUserDto>(`/admin/admins/${editingAdmin.id}`, {
        method: 'PATCH',
        body: editForm,
      });
      setEditingAdmin(null);
      await carregar();
    } catch (caught) {
      setModalError(
        caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao salvar edicao',
      );
    } finally {
      setEditingSubmitting(false);
    }
  }

  async function confirmarRedefinicaoSenha(event: React.FormEvent) {
    event.preventDefault();
    if (!resetPwdAdmin) return;
    setModalError(null);
    setResetSubmitting(true);

    try {
      await apiRequest<{ success: boolean }>(`/admin/admins/${resetPwdAdmin.id}/password`, {
        method: 'POST',
        body: { password: newPassword },
      });
      setFormOk(`Senha provisória de ${resetPwdAdmin.name} redefinida com sucesso.`);
      setResetPwdAdmin(null);
      setNewPassword('');
      await carregar();
    } catch (caught) {
      setModalError(
        caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao redefinir senha',
      );
    } finally {
      setResetSubmitting(false);
    }
  }

  async function confirmarExclusao() {
    if (!deletingAdmin) return;
    setModalError(null);
    setDeleteSubmitting(true);

    try {
      await apiRequest<{ success: boolean }>(`/admin/admins/${deletingAdmin.id}`, {
        method: 'DELETE',
      });
      setFormOk(`Administrador ${deletingAdmin.name} excluído.`);
      setDeletingAdmin(null);
      await carregar();
    } catch (caught) {
      setModalError(
        caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao excluir administrador',
      );
    } finally {
      setDeleteSubmitting(false);
    }
  }

  const mikrotikMap = new Map(mikrotiks.map((m) => [m.id, m.name]));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Administradores</h1>
        <p className="text-sm text-slate-500">
          Contas com acesso ao painel, seus perfis e escopo de equipamentos.
        </p>
      </div>

      <Card title="Contas cadastradas">
        {erroLista ? <Alert>{erroLista}</Alert> : null}
        {formOk ? <Alert tone="success">{formOk}</Alert> : null}

        {admins === null ? (
          <Spinner />
        ) : admins.length === 0 && !erroLista ? (
          <p className="text-sm text-slate-500">Nenhum administrador encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">E-mail</th>
                  <th className="py-2 pr-4">Perfil</th>
                  <th className="py-2 pr-4">Equipamentos</th>
                  <th className="py-2 pr-4">Situação</th>
                  <th className="py-2 pr-4">Último acesso</th>
                  {podeEscrever ? <th className="py-2 text-right">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => {
                  const nomesEquipamentos = admin.allowedMikrotikIds
                    ?.map((id) => mikrotikMap.get(id) ?? `#${id}`)
                    .join(', ');
                  const isCurrent = session?.admin?.id === admin.id;

                  return (
                    <tr key={admin.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                      <td className="py-2.5 pr-4 font-medium text-slate-800">
                        {admin.name}
                        {isCurrent ? (
                          <span className="ml-2 inline-flex items-center rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                            você
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600">{admin.email}</td>
                      <td className="py-2.5 pr-4 text-slate-600 font-medium">{admin.roleLabel}</td>
                      <td className="py-2.5 pr-4">
                        {admin.allMikrotiks ? (
                          <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                            Todos
                          </span>
                        ) : admin.allowedMikrotikIds && admin.allowedMikrotikIds.length > 0 ? (
                          <span
                            title={nomesEquipamentos}
                            className="inline-flex items-center rounded-md bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-700/20 cursor-help"
                          >
                            {admin.allowedMikrotikIds.length} equipamento(s)
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-md bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20">
                            Nenhum
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4">
                        {!admin.isActive ? (
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            inativo
                          </span>
                        ) : admin.mustChangePassword ? (
                          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                            troca pendente
                          </span>
                        ) : (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                            ativo
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600">
                        {admin.lastLoginAt
                          ? new Date(admin.lastLoginAt).toLocaleString('pt-BR')
                          : 'nunca'}
                      </td>
                      {podeEscrever ? (
                        <td className="py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => abrirEdicao(admin)}
                              title="Editar permissões e dados"
                            >
                              Editar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setModalError(null);
                                setNewPassword('');
                                setResetPwdAdmin(admin);
                              }}
                              title="Redefinir senha temporária"
                            >
                              Senha
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={isCurrent}
                              onClick={() => {
                                setModalError(null);
                                setDeletingAdmin(admin);
                              }}
                              title={isCurrent ? 'Não é possível excluir a própria conta' : 'Excluir administrador'}
                            >
                              Excluir
                            </Button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {podeEscrever ? (
        <Card title="Novo administrador">
          <form onSubmit={criar} className="grid gap-4 sm:grid-cols-2">
            {formError ? (
              <div className="sm:col-span-2">
                <Alert>{formError}</Alert>
              </div>
            ) : null}

            <Field
              label="Nome"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
              minLength={2}
            />

            <Field
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              required
            />

            <Field
              label="Senha provisoria"
              type="password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              required
              minLength={10}
              autoComplete="new-password"
              hint="Minimo 10 caracteres, com maiuscula, minuscula e numero."
            />

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Perfil</span>
              <select
                value={form.role}
                onChange={(event) => setForm({ ...form, role: event.target.value as RoleKey })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              >
                {ROLE_KEY_VALUES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </label>

            {/* Escopo de Equipamentos */}
            <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
              <span className="block text-sm font-semibold text-slate-800">
                Acesso aos Equipamentos (MikroTiks)
              </span>
              <div className="flex flex-col sm:flex-row gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="radio"
                    name="scope_mikrotik"
                    checked={form.allMikrotiks}
                    onChange={() => setForm({ ...form, allMikrotiks: true })}
                    className="size-4 text-brand-600 focus:ring-brand-500 border-slate-300"
                  />
                  <span>Todos os equipamentos</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="radio"
                    name="scope_mikrotik"
                    checked={!form.allMikrotiks}
                    onChange={() => setForm({ ...form, allMikrotiks: false })}
                    className="size-4 text-brand-600 focus:ring-brand-500 border-slate-300"
                  />
                  <span>Apenas equipamentos selecionados</span>
                </label>
              </div>

              {!form.allMikrotiks ? (
                <div className="mt-3 space-y-2 pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    Selecione quais MikroTiks este usuário poderá visualizar e gerenciar sessões/clientes:
                  </p>
                  {mikrotiks.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      Nenhum equipamento cadastrado no momento. Cadastre um MikroTik primeiro.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 max-h-48 overflow-y-auto p-1">
                      {mikrotiks.map((m) => {
                        const checked = form.mikrotikIds.includes(m.id);
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-2.5 p-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                              checked
                                ? 'border-brand-500 bg-brand-50/50 text-brand-900 font-medium'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMikrotik(m.id)}
                              className="size-4 rounded text-brand-600 focus:ring-brand-500 border-slate-300"
                            />
                            <span className="truncate">{m.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Criando...' : 'Criar administrador'}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Alert tone="info">Seu perfil permite apenas visualizar administradores.</Alert>
      )}

      {/* Modal de Edição */}
      {editingAdmin ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl space-y-5 my-8">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Editar Administrador</h3>
                <p className="text-xs text-slate-500">{editingAdmin.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setEditingAdmin(null)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1"
              >
                &times;
              </button>
            </div>

            {modalError ? <Alert>{modalError}</Alert> : null}

            <form onSubmit={salvarEdicao} className="space-y-4">
              <Field
                label="Nome"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                minLength={2}
              />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Perfil</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as RoleKey })}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                >
                  {ROLE_KEY_VALUES.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    disabled={session?.admin?.id === editingAdmin.id}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    className="size-4 rounded text-brand-600 focus:ring-brand-500 border-slate-300"
                  />
                  <span className="font-medium">Conta ativa</span>
                </label>
                {session?.admin?.id === editingAdmin.id ? (
                  <p className="text-xs text-slate-400 mt-1">Você não pode desativar sua própria conta.</p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Desativar a conta revoga imediatamente o login e todas as sessões ativas do usuário.
                  </p>
                )}
              </div>

              {/* Equipamentos no modal */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-3">
                <span className="block text-sm font-semibold text-slate-800">
                  Acesso aos Equipamentos (MikroTiks)
                </span>
                <div className="flex flex-col sm:flex-row gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                    <input
                      type="radio"
                      name="edit_scope_mikrotik"
                      checked={editForm.allMikrotiks}
                      onChange={() => setEditForm({ ...editForm, allMikrotiks: true })}
                      className="size-4 text-brand-600 focus:ring-brand-500 border-slate-300"
                    />
                    <span>Todos os equipamentos</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                    <input
                      type="radio"
                      name="edit_scope_mikrotik"
                      checked={!editForm.allMikrotiks}
                      onChange={() => setEditForm({ ...editForm, allMikrotiks: false })}
                      className="size-4 text-brand-600 focus:ring-brand-500 border-slate-300"
                    />
                    <span>Apenas selecionados</span>
                  </label>
                </div>

                {!editForm.allMikrotiks ? (
                  <div className="mt-2 space-y-2 pt-2 border-t border-slate-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-1">
                      {mikrotiks.map((m) => {
                        const checked = editForm.mikrotikIds.includes(m.id);
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-2 p-2 rounded-lg border text-sm cursor-pointer ${
                              checked
                                ? 'border-brand-500 bg-brand-50/50 text-brand-900 font-medium'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEditMikrotik(m.id)}
                              className="size-4 rounded text-brand-600 focus:ring-brand-500 border-slate-300"
                            />
                            <span className="truncate">{m.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setEditingAdmin(null)}
                  disabled={editingSubmitting}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={editingSubmitting}>
                  {editingSubmitting ? 'Salvando...' : 'Salvar alterações'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Modal de Redefinir Senha */}
      {resetPwdAdmin ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Redefinir Senha Provisória</h3>
                <p className="text-xs text-slate-500">{resetPwdAdmin.name} ({resetPwdAdmin.email})</p>
              </div>
              <button
                type="button"
                onClick={() => setResetPwdAdmin(null)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1"
              >
                &times;
              </button>
            </div>

            {modalError ? <Alert>{modalError}</Alert> : null}

            <form onSubmit={confirmarRedefinicaoSenha} className="space-y-4">
              <Field
                label="Nova senha provisória"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={10}
                autoComplete="new-password"
                hint="Mínimo 10 caracteres, com maiúscula, minúscula e número. O usuário deverá trocá-la no próximo acesso."
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setResetPwdAdmin(null)}
                  disabled={resetSubmitting}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={resetSubmitting || newPassword.length < 10}>
                  {resetSubmitting ? 'Redefinindo...' : 'Redefinir senha'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Modal de Confirmação de Exclusão */}
      {deletingAdmin ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Excluir Administrador</h3>
            <p className="text-sm text-slate-600">
              Tem certeza que deseja excluir permanentemente o administrador{' '}
              <strong className="text-slate-900">{deletingAdmin.name}</strong> ({deletingAdmin.email})?
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200">
              Esta ação revoga todas as sessões do usuário e remove suas permissões de acesso ao painel.
            </p>

            {modalError ? <Alert>{modalError}</Alert> : null}

            <div className="flex justify-end gap-2 pt-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeletingAdmin(null)}
                disabled={deleteSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={confirmarExclusao}
                disabled={deleteSubmitting}
              >
                {deleteSubmitting ? 'Excluindo...' : 'Sim, excluir'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
